<template>
  <div>
    <!-- Navbar -->
    <navbar :links="['dashboard', $route['name']]" />
    <div
      :class="{
        ...$desktopModeStyles,
        'elevation-1': scrollTop > fixedSearchThreshold,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
      }"
    >
      <!-- Content Filters: ctype, active, published, Groups, ... -->
      <content-type-filter-menu
        ref="menu"
        :class="{'mr-3': onlyTickerContentTypeSelected}"
        :no-radio="$route.params.ctype === 'tv'"
        :on-air-only="contentsQuery.onAirOnly"
        :force-dirty="groupFilterActive"
        :title="$t('filterContentByVarious')"
        data-cy="content-type-filter-menu"
      >
        <div slot="extraItems">
          <group-filter-menu-items
            v-if="groupsToFilterContentBy.length > 0"
            :groups="groupsToFilterContentBy"
            :selected-group-ids="contentsQuerySelectedGroupIds"
            @change="onGroupFilterChanged"
          />
        </div>
      </content-type-filter-menu>
      <!-- Categories Filter Menu -->
      <v-slide-x-transition>
        <spot-category-filter-menu
          v-if="!onlyTickerContentTypeSelected"
          v-model="contentsQuerySelectedCategoryIds"
          :categories="categories"
          :active="contentsQuerySelectedCategoryIds.length > 0"
          class="ml-1"
          :class="{'mr-3': !$userprofile || !$userprofile.can_use_tagging}"
          :title="$t('filterCategoriesTooltip')"
        />
      </v-slide-x-transition>
      <!-- Tags Filter Menu -->
      <v-slide-x-transition>
        <tag-filter-menu
          v-if="
            !onlyTickerContentTypeSelected &&
              $userprofile &&
              $userprofile.can_use_tagging
          "
          v-model="contentsQuerySelectedTagIds"
          :tags="$store.state.common.tags"
          :active="contentsQuerySelectedTagIds.length > 0"
          :title="$t('filterTagsTooltip')"
          class="ml-1 mr-3 pt-3"
        />
      </v-slide-x-transition>
      <!-- Search -->
      <v-text-field
        ref="searchbar"
        v-model="contentsQuerySearchText"
        class="searchbar-input"
        hide-details
        :label="searchLabel"
        clearable
        append-icon="search"
        @click:clear="clearSearch()"
        @input="autoSearch()"
        @keyup.native.enter="fetchNextContents()"
        @keyup.native.esc="clearSearch()"
      />
    </div>
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <!-- 'No Brands chosen yet' notice -->
    <v-alert
      :value="noBrandsSelectedYetAlthoughAvailable"
      outline
      icon="info"
      color="msr"
      dismissible
    >
      {{ $t('noBrandsSelectedYetAlthoughAvailable_1') }}
      <router-link :to="'brands'">
        {{ $t('brandSelection') }}
      </router-link>
      {{ $t('noBrandsSelectedYetAlthoughAvailable_2') + '.' }}
    </v-alert>
    <div class="mb-3">
      <!-- Active Content Filters -->
      <content-type-filter-active-chips />
      <!-- Matched Brand Groups -->
      <brand-group-search-matches :search-text="contentsQuerySearchText" />
    </div>
    <!-- 'No search results' -->
    <v-list
      v-if="!loading && !contents.length"
      data-cy="no-contents-matching-search-info"
    >
      <v-list-tile>
        <v-list-tile-sub-title style="white-space: initial">
          {{ $t('noSpotsForSearch') }}
          <span v-if="lastContentsQuery.selectedCategoryIds.length > 0">
            ({{ lastContentsQuery.selectedCategoryIds.length }}
            {{ $t('categories') }})
          </span>
          <br>
          <span class="msr--text current-search">
            <!-- TODO: Add info about chosen content types -->
            {{ lastContentsQuery.searchText }}
          </span>
        </v-list-tile-sub-title>
      </v-list-tile>
    </v-list>
    <!-- Content list (RadioSpot, TVSpot, TickerMessage) -->
    <div
      v-for="(content, index) in contents"
      id="scrollable-contents-list"
      :key="content.id"
    >
      <spot
        v-if="isSpot(content)"
        :spot="content"
        :ctype="content.ctype"
        show-content-selection-checkbox
        :show-divider="index !== contents.length - 1"
      />
      <tv-spot-bundle
        v-if="isTVSpotBundle(content)"
        :bundle="content"
        enable-edit-overlay-data-shortcut
        show-content-selection-checkbox
        :show-divider="index !== contents.length - 1"
      />
      <ticker
        v-if="isTicker(content)"
        :ticker="content"
        show-content-selection-checkbox
        :show-divider="index !== contents.length - 1"
      />
    </div>
    <!-- Infinite Scroll Spy -->
    <mugen-scroll
      :handler="onInfiniteScrollTriggered"
      :handle-on-mount="false"
      :should-handle="!$store.state.common.progress._active"
    />
    <!-- Additional Scrolling Space -->
    <div
      v-if="!loading && !morePagesAvailable"
      style="min-height: 100px"
    />
    <!-- Loading Indicator -->
    <div
      id="loading-indicator"
      class="loading-indicator"
    >
      <v-progress-circular
        v-if="loading || morePagesAvailable"
        id="content-loading-indicator"
        indeterminate
        :size="50"
        class="mt-5 msr--text"
      />
    </div>
    <!-- Create Ticker button -->
    <v-slide-x-reverse-transition>
      <create-ticker-button
        v-if="showCreateTickerButton"
        @click="showCreateTickerDialog = true"
      />
    </v-slide-x-reverse-transition>
    <!-- Upload Content button -->
    <v-slide-x-reverse-transition>
      <v-btn
        v-if="showContentUploadButton"
        :fab="$vuetify.breakpoint.xs"
        :round="$vuetify.breakpoint.smAndUp"
        dark
        fixed
        bottom
        right
        data-cy="upload-content-btn"
        class="msr z-index-3"
        :class="$desktopModeStyles"
        @click.stop="$router.push('/upload')"
      >
        <v-icon :left="$vuetify.breakpoint.smAndUp">
          file_upload
        </v-icon>
        <span v-if="$vuetify.breakpoint.smAndUp">{{
          $t('uploadContent')
        }}</span>
      </v-btn>
    </v-slide-x-reverse-transition>
    <!-- Placeholder Add Content Button for people that need an uploadgroup -->
    <v-slide-x-reverse-transition>
      <v-btn
        v-if="
          !isAllowedToUploadContent &&
            canRequestUploadFeature &&
            !updateFeatureRequested
        "
        fab
        dark
        fixed
        hover
        right
        bottom
        style="z-index: 3"
        class="info"
        :class="$desktopModeStyles"
        @click="showRequestUpdateFeatureDialog = true"
      >
        <v-icon medium>
          add
        </v-icon>
      </v-btn>
    </v-slide-x-reverse-transition>
    <!-- Content Selection Overlay -->
    <content-selection-overlay
      ref="contentselectionoverlay"
      :show="showContentSelectionOverlay"
      :contents="contents"
      :loading="busyProcessingSelectedContents"
      @aborted="onContentSelectionOverlayAborted"
      @selectionconfirmed="onContentSelectionOverlayConfirmed"
    />
    <!-- Request Upload Feature Dialog -->
    <v-dialog v-model="showRequestUpdateFeatureDialog">
      <v-card>
        <v-card-title>
          <v-layout align-center>
            <div class="headline">
              {{ $t('requestUploadFeatureDialogTitle') }}
            </div>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('cancel')"
              @click="showRequestUpdateFeatureDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>{{ $t('requestUploadFeatureDialogHint') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            @click="requestUploadFeature()"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Create Ticker Dialog -->
    <create-ticker-dialog
      v-if="isAllowedToCreateTickers"
      ref="createTickerDialog"
      :show="showCreateTickerDialog"
      :uploadgroups="$store.getters.uploadgroups"
      @toggled="(val) => (showCreateTickerDialog = val)"
      @submitted="createTicker"
      @aborted="abortTickerCreation()"
    />
    <!-- Content Deletion Warnings Dialog -->
    <content-deletion-warnings-dialog
      :show.sync="showContentSelectionOverlayDeletionReportDialog"
      :contents="contentSelectionOverlayDeletionSelectedContents"
      :warnings="contentSelectionOverlayDeletionWarnings"
      @close="
        contentSelectionOverlayDeletionSelectedContents = [];
        contentSelectionOverlayDeletionWarnings = [];
        showContentSelectionOverlayDeletionReportDialog = false;
      "
    />
  </div>
</template>

<script>
import MugenScroll from 'vue-mugen-scroll';

import {
  getMainTVSpotFromBundle,
  removeUrlQueryParamsFromCurrentRoute,
} from '@/utils';
import contentSelectionOverlay from '@/mixins/contentSelectionOverlay.js';
import fetchContents from '@/mixins/fetchContents.js';
import identifiers from '@/mixins/identifiers.js';
import rememberPageScrollPos from '@/mixins/rememberPageScrollPos.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import BrandGroupSearchMatches from '@/components/BrandGroupSearchMatches';
import BtnOk from '@/components/ButtonOk';
import ContentDeletionWarningsDialog from '@/components/ContentDeletionWarningsDialog';
import ContentSelectionOverlay from '@/components/ContentSelectionOverlay';
import ContentTypeFilterActiveChips from '@/components/ContentTypeFilterActiveChips';
import ContentTypeFilterMenu from '@/components/ContentTypeFilterMenu';
import CreateTickerButton from '@/components/CreateTickerButton';
import CreateTickerDialog from '@/components/CreateTickerDialog';
import GroupFilterMenuItems from '@/components/GroupFilterMenuItems';
import Navbar from '@/components/Navbar';
import Spot from '@/components/Spot';
import SpotCategoryFilterMenu from '@/components/SpotCategoryFilterMenu';
import TagFilterMenu from '@/components/TagFilterMenu';
import Ticker from '@/components/Ticker';
import TvSpotBundle from '@/components/TvSpotBundle';

export default {
  components: {
    MugenScroll,

    BrandGroupSearchMatches,
    BtnOk,
    ContentDeletionWarningsDialog,
    ContentSelectionOverlay,
    ContentTypeFilterActiveChips,
    ContentTypeFilterMenu,
    CreateTickerButton,
    CreateTickerDialog,
    GroupFilterMenuItems,
    Navbar,
    Spot,
    SpotCategoryFilterMenu,
    TagFilterMenu,
    Ticker,
    TvSpotBundle,
  },
  mixins: [
    contentSelectionOverlay,
    fetchContents,
    identifiers,
    rememberPageScrollPos,
    trackScrollTop,
  ],
  data() {
    return {
      showContentSelectionOverlayDeletionReportDialog: false,
      showContentUploadDialog: false,
      showCreateTickerDialog: false,
      showRequestUpdateFeatureDialog: false,

      // Used to hide the request button after submission, at least
      // while on the same page.
      updateFeatureRequested: false,

      contentSelectionOverlayDeletionSelectedContents: [],
      contentSelectionOverlayDeletionWarnings: [],

      unwatchPreselectContents: null,
    };
  },
  computed: {
    groupsToFilterContentBy() {
      return this.$store.getters.accessibleGroups.filter((group) => {
        return (
          group.kind !== 'INDUSTRY' && group.visible_in_app_content_filters
        );
      });
    },
    categories() {
      return this.$store.state.common.categories;
    },
    canRequestUploadFeature() {
      return this.$userprofile && this.$userprofile.can_request_upload_feature;
    },
    isAllowedToUploadContent() {
      return this.$store.getters.userManagesContentForAtLeastOneGroup;
    },
    isAllowedToCreateTickers() {
      return this.$store.getters.userCanCreateTickers;
    },
    showCreateTickerButton() {
      return this.isAllowedToCreateTickers && !this.showContentSelectionOverlay;
    },
    showContentUploadButton() {
      return this.isAllowedToUploadContent && !this.showContentSelectionOverlay;
    },
    onlyTickerContentTypeSelected() {
      return this.ctypes.length === 1 && this.ctypes[0] === 'tickers';
    },
    searchLabel() {
      return `${this.$t('search')} (${this.lastQueryCount}) ...`;
    },
    contentsQuerySelectedGroupIds() {
      return this.$store.state.common.contentsQuery.selectedGroupIds;
    },
    groupFilterActive() {
      return this.contentsQuerySelectedGroupIds.length > 0;
    },
    morePagesAvailable() {
      return (
        this.contents.length > 0 && this.contentsPage < this.contentsNumPages
      );
    },
    noBrandsSelectedYetAlthoughAvailable() {
      return (
        this.$userprofile &&
        this.$userprofile.available_brand_groups.length > 0 &&
        this.$userprofile.chosen_brand_groups.length === 0
      );
    },
  },
  watch: {
    /**
     * Workaround for: https://github.com/myshopradio/cms/issues/602
     *
     * We either refetched or fetched a new page of contents: If the
     * progress-spinner is still visible, we may be inside a browser
     * window with a height that is larger than the list of contents we
     * display, which means the vue-mugen-scroll event cannot be
     * triggered through scrolling anymore.
     *
     * Fetch some more content to fix that.
     */
    'contents.length': function () {
      this.$nextTick(() => {
        const spinnerVisible = this.isElementInViewport('loading-indicator');
        if (spinnerVisible && this.morePagesAvailable) {
          this.fetchNextContents();
        }
      });
    },
  },
  mounted() {
    if (this.contentsQueryHasChanged) {
      console.log('[DEBUG] Invalidating contents cache due to query change');
      this.refetchContents();
    }

    this.fetchCategories();
    this.updateContentUnseen();

    this.$on(
      'contentSelectionOverlayDeletionReport',
      this.onContentSelectionOverlayDeletionReport
    );
  },
  beforeDestroy() {
    this.removeWatchPreselectContents();
  },
  methods: {
    removeWatchPreselectContents() {
      if (this.unwatchPreselectContents) {
        this.unwatchPreselectContents();
        this.unwatchPreselectContents = null;
      }
    },
    preselectViaQueryParamAfterContentsFetched() {
      if (!this.$route.query || !this.$route.query.preselect) {
        return;
      }

      const vm = this;

      function preselectContent() {
        if (!vm.$route.query || !vm.$route.query.preselect) {
          return;
        }

        // For TV, the idents target the main TVSpot, not the bundle, e.g.:
        // /app/content?preselect=tvspot_93386
        const idents = vm.$route.query.preselect.split(',');

        let lastSelectedSpot = null;
        for (const content of vm.contents) {
          let spot = content;
          if (vm.isTVSpotBundle(content)) {
            spot = getMainTVSpotFromBundle(content);
          }
          if (idents.includes(spot.ident)) {
            spot.selected = true;
            lastSelectedSpot = spot;

            if (vm.$route.query.mode) {
              vm.$nextTick(() => {
                vm.$store.commit(
                  'SET_CONTENT_SELECTION_OVERLAY_MODE',
                  vm.$route.query.mode.toUpperCase() // e.g. 'DEACTIVATE'
                );
              });
            }
          }
        }

        // Scroll to last matched content.
        if (lastSelectedSpot) {
          const lastSelectedSpotElement = document.getElementById(
            lastSelectedSpot.ident
          );
          if (lastSelectedSpotElement) {
            lastSelectedSpotElement.scrollIntoView({
              block: 'center',
              behavior: 'smooth',
            });
          }
        }

        const weMatched = !!lastSelectedSpot;
        if (weMatched) {
          vm.removeWatchPreselectContents();
          removeUrlQueryParamsFromCurrentRoute();
        }
      }

      // This behaviour here should be triggered as part of initial
      // navigation (e.g. link in an email) where we are busy fetching
      // the contents list and need to wait until that is available for
      // selection.
      this.unwatchPreselectContents = this.$watch(
        '$store.state.common.contents',
        function () {
          preselectContent();
        }
      );
    },
    onContentSelectionOverlayDeletionReport(context) {
      this.contentSelectionOverlayDeletionSelectedContents =
        context.selectedContents;
      this.contentSelectionOverlayDeletionWarnings = context.warnings;
      this.showContentSelectionOverlayDeletionReportDialog = true;
    },
    /* Based on: https://stackoverflow.com/questions/123999 */
    isElementInViewport(id, partial = true) {
      const el = document.getElementById(id);
      if (!el) {
        return false;
      }
      const rect = el.getBoundingClientRect();

      let maxBottom =
        window.innerHeight || document.documentElement.clientHeight;
      let maxRight = window.innerWidth || document.documentElement.clientWidth;
      if (partial) {
        maxBottom += rect.height - 1;
        maxRight += rect.width - 1;
      }
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= maxBottom &&
        rect.right <= maxRight
      );
    },
    onGroupFilterChanged(group, selected) {
      let mutation;
      if (selected) {
        mutation = 'ADD_CONTENTS_QUERY_SELECTED_GROUP_ID';
      } else {
        mutation = 'REMOVE_CONTENTS_QUERY_SELECTED_GROUP_ID';
      }
      this.$store.commit(mutation, group.id);
    },
    focusSearch() {
      this.$refs.searchbar.focus();
    },
    clearSearch() {
      this.contentsQuerySearchText = '';
      this.refetchContents();
    },
    onInfiniteScrollTriggered() {
      this.fetchNextContents();
    },
    createTicker(message, groupId, routeToEntryActivationAfterwards = true) {
      const dialog = this.$refs.createTickerDialog;
      dialog.busy = true;
      this.$api.createTicker(message, groupId).then((response) => {
        this.$bus.$emit('alert-success', {
          message: 'tickerCreatedSuccess',
          timeout: 2000,
          translate: true,
        });

        dialog.busy = false;
        dialog.reset();
        this.showCreateTickerDialog = false;

        this.refetchContents();

        if (routeToEntryActivationAfterwards) {
          const ticker = response.data;
          this.$router.push({
            path: '/ticker/' + ticker.id + '/add',
          });
        }
      });
    },
    abortTickerCreation() {
      this.$refs.createTickerDialog.reset();
      this.showCreateTickerDialog = false;
    },
    /**
     * Tell support that a User wants to be able to use the Upload feature.
     * He doesn't have an upload group yet, so we'll have to prepare
     * some things in the backend and get back to him.
     */
    requestUploadFeature() {
      this.$api.requestUploadFeature().then((response) => {
        this.updateFeatureRequested = true;
        this.showRequestUpdateFeatureDialog = false;

        this.$bus.$emit('alert-success', {
          message: 'requestUploadFeatureSubmitted',
          translate: true,
          timeout: 12000,
        });
      });
    },
    fetchCategories() {
      return this.$store.dispatch('LIST_CATEGORIES');
    },
    updateContentUnseen() {
      this.$api.updateUserLastSeen('content').then(() => {
        this.$store.dispatch('RETRIEVE_NUM_UNSEEN');
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.preselectViaQueryParamAfterContentsFetched();
    });
  },
};
</script>

<style scoped>
.tools {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 18px;
}

.tools-fixed {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 40px;
  left: 0;
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 8px;
  background-color: white;
  z-index: 2; /* Fix .v-chip__content overlapping with x-index of 1. */
}

.tools-fixed-spacer {
  margin-bottom: 70px;
}

.searchbar-input {
  max-width: 400px;
}

.current-search {
  display: flex;
  justify-content: center;
}

/* Fix category v-select icon not colored when filtering is active. */
div >>> .v-select.msr--text i {
  color: var(--msr-color-primary) !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/Contents.vue